import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router';

import { mutationEditorReset } from 'gql/mutations/editor';
import { useMutationLMSGradeSynchronize } from 'gql/mutations/lms';
import { useQueryCourseStudents } from 'gql/queries/course';
import {
  useQueryResultsAggregated,
  useQueryResultsAggregatedByGroup,
  useQueryResultsAggregatedByMe,
  useQueryResultsAggregatedByStudent,
  useQueryResultsStudyStatistics,
  useQueryResultsStudyStatisticsByGroup,
  useQueryResultsStudyStatisticsByMe,
  useQueryResultsStudyStatisticsByStudent,
} from 'gql/queries/results';
import history from 'lib/history';
import { trackEvent, reportError } from 'lib/tracking';
import { notify } from 'uikit/Notifications';

import Statistics from './Statistics';

const StatisticsContainer = (props) => {
  const { t } = useTranslation();
  const { courseId } = useParams();
  const { isLoading: isLoadingCourse, data: dataCourse } =
    useQueryCourseStudents({
      variables: {
        courseId,
      },
    });
  const groups = useMemo(
    () => (isLoadingCourse ? [] : dataCourse.course.groups),
    [dataCourse, isLoadingCourse],
  );
  const [studentInputValue, setStudentInputValue] = useState('');
  const handleStudentInputChange = useCallback(
    (event) => setStudentInputValue(event.target.value),
    [],
  );
  const students = useMemo(
    () =>
      isLoadingCourse
        ? []
        : [].concat(
            dataCourse.course.students,
            ...dataCourse.course.groups.map((g) => g.students),
          ),
    [dataCourse, isLoadingCourse],
  );
  const filteredStudents = useMemo(
    () =>
      students.filter(
        (student) =>
          `${student.lastName.toUpperCase()}, ${student.firstName}`
            .toLowerCase()
            .includes(studentInputValue.toLowerCase()) ||
          studentInputValue === '' ||
          students.some(
            (s) =>
              `${s.lastName.toUpperCase()}, ${s.firstName}`.toLowerCase() ===
              studentInputValue.toLowerCase(),
          ),
      ),
    [studentInputValue, students],
  );
  const hasNoStudents = students.length === 0;
  const hasNoGroups = groups.length === 0;
  const { state } = useLocation();
  const type = state?.type || (hasNoStudents ? 'me' : 'full');
  const display = state?.display || 'statistics';
  const groupId = state?.groupId || null;
  const studentId = state?.studentId || null;
  const {
    getResultsStudyStatistics,
    isLoading: isLoadingFull,
    data: dataFull,
  } = useQueryResultsStudyStatistics();
  const {
    getResultsStudyStatisticsByGroup,
    isLoading: isLoadingGroup,
    data: dataGroup,
  } = useQueryResultsStudyStatisticsByGroup();
  const {
    getResultsStudyStatisticsByMe,
    isLoading: isLoadingMe,
    data: dataMe,
  } = useQueryResultsStudyStatisticsByMe();
  const {
    getResultsStudyStatisticsByStudent,
    isLoading: isLoadingStudent,
    data: dataStudent,
  } = useQueryResultsStudyStatisticsByStudent();
  const {
    getResultsAggregated,
    isLoading: isLoadingReportFull,
    data: dataReportFull,
  } = useQueryResultsAggregated();
  const {
    getResultsAggregatedByGroup,
    isLoading: isLoadingReportGroup,
    data: dataReportGroup,
  } = useQueryResultsAggregatedByGroup();
  const {
    getResultsAggregatedByMe,
    isLoading: isLoadingReportMe,
    data: dataReportMe,
  } = useQueryResultsAggregatedByMe();
  const {
    getResultsAggregatedByStudent,
    isLoading: isLoadingReportStudent,
    data: dataReportStudent,
  } = useQueryResultsAggregatedByStudent();
  useEffect(() => {
    if (display === 'statistics' && type === 'student') {
      getResultsStudyStatisticsByStudent({
        variables: {
          studentId,
        },
      });
    } else if (display === 'statistics' && type === 'group') {
      getResultsStudyStatisticsByGroup({
        variables: {
          groupId,
        },
      });
    } else if (display === 'statistics' && type === 'me') {
      getResultsStudyStatisticsByMe({
        variables: {
          courseId,
        },
      });
    } else if (display === 'statistics' && type === 'full') {
      getResultsStudyStatistics({
        variables: {
          courseId,
        },
      });
    } else if (display === 'report' && type === 'student') {
      getResultsAggregatedByStudent({
        variables: {
          studentId,
        },
      });
    } else if (display === 'report' && type === 'group') {
      getResultsAggregatedByGroup({
        variables: {
          groupId,
        },
      });
    } else if (display === 'report' && type === 'me') {
      getResultsAggregatedByMe({
        variables: {
          courseId,
        },
      });
    } else if (display === 'report' && type === 'full') {
      getResultsAggregated({
        variables: {
          courseId,
        },
      });
    }
  }, [
    courseId,
    display,
    getResultsAggregated,
    getResultsAggregatedByGroup,
    getResultsAggregatedByMe,
    getResultsAggregatedByStudent,
    getResultsStudyStatistics,
    getResultsStudyStatisticsByGroup,
    getResultsStudyStatisticsByMe,
    getResultsStudyStatisticsByStudent,
    groupId,
    studentId,
    type,
  ]);
  const isLoading =
    isLoadingCourse ||
    (display === 'statistics' && type === 'student'
      ? isLoadingStudent
      : display === 'statistics' && type === 'group'
      ? isLoadingGroup
      : display === 'statistics' && type === 'me'
      ? isLoadingMe
      : display === 'statistics' && type === 'full'
      ? isLoadingFull
      : display === 'report' && type === 'student'
      ? isLoadingReportStudent
      : display === 'report' && type === 'group'
      ? isLoadingReportGroup
      : display === 'report' && type === 'me'
      ? isLoadingReportMe
      : isLoadingReportFull);
  const data =
    display === 'statistics'
      ? type === 'student'
        ? dataStudent?.resultsStudyStatisticsByStudent
        : type === 'group'
        ? dataGroup?.resultsStudyStatisticsByGroup
        : type === 'me'
        ? dataMe?.resultsStudyStatisticsByMe
        : dataFull?.resultsStudyStatistics
      : type === 'student'
      ? dataReportStudent?.resultsAggregatedByStudent
      : type === 'group'
      ? dataReportGroup?.resultsAggregatedByGroup
      : type === 'me'
      ? dataReportMe?.resultsAggregatedByMe
      : dataReportFull?.resultsAggregated;
  const handleChangeType = useCallback(
    (ty) => {
      if (ty === 'student') {
        history.push({
          pathname: `/course/${courseId}/statistics`,
          state: {
            display,
            type: 'student',
            studentId: students && students[0] && students[0].id,
          },
        });
      } else if (ty === 'group') {
        history.push({
          pathname: `/course/${courseId}/statistics`,
          state: {
            display,
            type: 'group',
            groupId: groups && groups[0] && groups[0].id,
          },
        });
      } else if (ty === 'me') {
        history.push({
          pathname: `/course/${courseId}/statistics`,
          state: {
            display,
            type: 'me',
          },
        });
      } else {
        history.push({
          pathname: `/course/${courseId}/statistics`,
          state: {
            display,
            type: 'full',
          },
        });
      }
    },
    [courseId, display, groups, students],
  );
  const handleChangeDisplay = useCallback(() => {
    if (type === 'student') {
      history.push({
        pathname: `/course/${courseId}/statistics`,
        state: {
          display: display === 'statistics' ? 'report' : 'statistics',
          type: 'student',
          studentId: students && students[0] && students[0].id,
        },
      });
    } else if (type === 'group') {
      history.push({
        pathname: `/course/${courseId}/statistics`,
        state: {
          display: display === 'statistics' ? 'report' : 'statistics',
          type: 'group',
          groupId: groups && groups[0] && groups[0].id,
        },
      });
    } else if (type === 'me') {
      history.push({
        pathname: `/course/${courseId}/statistics`,
        state: {
          display: display === 'statistics' ? 'report' : 'statistics',
          type: 'me',
        },
      });
    } else {
      history.push({
        pathname: `/course/${courseId}/statistics`,
        state: {
          display: display === 'statistics' ? 'report' : 'statistics',
          type: 'full',
        },
      });
    }
  }, [courseId, display, groups, students, type]);
  const selectedStudent = useMemo(
    () =>
      isLoading || type !== 'student'
        ? undefined
        : students.find((student) => student.id === state?.studentId) || [
            students[0],
          ],
    [isLoading, state, students, type],
  );
  const handleStudentInputBlur = useCallback(() => {
    if (selectedStudent) {
      setStudentInputValue(
        `${selectedStudent.lastName.toUpperCase()}, ${
          selectedStudent.firstName
        }`,
      );
    }
  }, [selectedStudent]);
  const handleStudentSelect = useCallback(
    (sId) => {
      history.push({
        pathname: `/course/${courseId}/statistics`,
        state: {
          display,
          type: 'student',
          studentId: sId,
        },
      });
    },
    [courseId, display],
  );
  useEffect(() => {
    if (selectedStudent) {
      setStudentInputValue(
        `${selectedStudent.lastName.toUpperCase()}, ${
          selectedStudent.firstName
        }`,
      );
    }
  }, [selectedStudent]);
  const [groupInputValue, setGroupInputValue] = useState('');
  const handleGroupInputChange = useCallback(
    (event) => setGroupInputValue(event.target.value),
    [],
  );
  const selectedGroup = useMemo(
    () =>
      isLoading || type !== 'group'
        ? undefined
        : dataCourse.course.groups.find((group) => group.id === state?.groupId),
    [isLoading, dataCourse, type, state],
  );
  const handleGroupInputBlur = useCallback(() => {
    if (selectedGroup) {
      setGroupInputValue(selectedGroup.title);
    }
  }, [selectedGroup]);
  const handleGroupSelect = useCallback(
    (gId) => {
      history.push({
        pathname: `/course/${courseId}/statistics`,
        state: {
          display,
          type: 'group',
          groupId: gId,
        },
      });
    },
    [courseId, display],
  );
  useEffect(() => {
    if (selectedGroup) {
      setGroupInputValue(selectedGroup.title);
    }
  }, [selectedGroup]);
  const filteredGroups = useMemo(
    () =>
      groups.filter(
        (group) =>
          group.title.toLowerCase().includes(groupInputValue.toLowerCase()) ||
          groupInputValue === '' ||
          groups.some(
            (g) => g.title.toLowerCase() === groupInputValue.toLowerCase(),
          ),
      ),
    [groupInputValue, groups],
  );
  const mutationLMSGradeSynchronize = useMutationLMSGradeSynchronize();
  const handleGradeSynchronize = useCallback(async () => {
    try {
      await mutationLMSGradeSynchronize({ courseId });
      trackEvent('LMS', 'grade synchronize');
      notify('success', t('Course/Teacher/Statistics/grade-synchronized'));
    } catch (err) {
      reportError('LMS', 'grade synchronize', err);
    }
  }, [courseId, mutationLMSGradeSynchronize, t]);
  useEffect(() => {
    mutationEditorReset();
    return () => {
      mutationEditorReset();
    };
  }, []);
  return (
    <Statistics
      course={dataCourse?.course}
      data={data}
      display={display}
      groupInputValue={groupInputValue}
      handleChangeDisplay={handleChangeDisplay}
      handleChangeType={handleChangeType}
      handleGradeSynchronize={handleGradeSynchronize}
      handleGroupInputBlur={handleGroupInputBlur}
      handleGroupInputChange={handleGroupInputChange}
      handleGroupSelect={handleGroupSelect}
      handleStudentInputBlur={handleStudentInputBlur}
      handleStudentInputChange={handleStudentInputChange}
      handleStudentSelect={handleStudentSelect}
      hasNoGroups={hasNoGroups}
      hasNoStudents={hasNoStudents}
      isLoading={isLoading}
      selectedGroup={selectedGroup}
      selectedStudent={selectedStudent}
      studentInputValue={studentInputValue}
      filteredGroups={filteredGroups}
      filteredStudents={filteredStudents}
      t={t}
      type={type}
      {...props}
    />
  );
};

export default StatisticsContainer;
